import { FC, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';

import { HasPermission } from '@components/auth';
import { Button, Switch } from '@components/base';
import usePrevious from '@hooks/usePrevious';
import { getDisableCache, globalVarsActions } from '@store/globalVars/slice';
import { useAppDispatch, useAppSelector } from '@store/hooks';

const DEFAULT_DURATION_MS = 60_000;

const formatTime = (ms: number) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const DisableCacheSwitch: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const disableCache = useAppSelector(getDisableCache);

  const timerRef = useRef<null | number>(null);
  const [displayCacheBackSnack, setDisplayCacheBackSnack] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const prevTimeLeft = usePrevious(timeLeft);

  const startCountdown = (duration = DEFAULT_DURATION_MS) => {
    if (timerRef.current != null) return;
    setTimeLeft(duration);
    timerRef.current = window.setInterval(() => {
      setTimeLeft(prev => {
        const next = prev - 1000;
        return next > 0 ? next : 0;
      });
    }, 1000);
  };

  const stopCountdown = () => {
    if (timerRef.current != null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimeLeft(0);
  };

  const handleDisableCache = () => {
    if (displayCacheBackSnack) setDisplayCacheBackSnack(false);
    dispatch(globalVarsActions.setDisableCache(true));
    startCountdown();
  };

  const handleEnableCache = () => {
    dispatch(globalVarsActions.setDisableCache(false));
    stopCountdown();
  };

  // Clear timer if `disableCache` is turned off elsewhere.
  useEffect(() => {
    if (!disableCache) stopCountdown();
  }, [disableCache]);

  // When countdown reaches zero from a positive value, enable cache and show snack.
  useEffect(() => {
    if (timeLeft === 0 && prevTimeLeft && prevTimeLeft > 0) {
      dispatch(globalVarsActions.setDisableCache(false));
      stopCountdown();
      setDisplayCacheBackSnack(true);
    }
  }, [timeLeft, prevTimeLeft, dispatch]);

  useEffect(() => {
    return () => {
      stopCountdown();
    };
  }, []);

  return (
    <HasPermission>
      <Switch
        checked={disableCache}
        hint={<Trans components={[<strong />]} i18nKey={t('disableCache.hint')} />}
        label={t('disableCache')}
        onChange={(_, checked) => (checked ? handleDisableCache() : handleEnableCache())}
      />

      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={timeLeft > 0}>
        <Alert
          action={
            <Button color='inherit' onClick={handleEnableCache}>
              {t('disableCache.enableNow')}
            </Button>
          }
          icon={undefined}
          severity='warning'
          variant='filled'
        >
          {t('disableCache.disabled', { time: formatTime(timeLeft) })}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={4000}
        color='info'
        onClose={() => setDisplayCacheBackSnack(false)}
        open={displayCacheBackSnack}
      >
        <Alert icon={undefined} severity='info' variant='filled'>
          {t('disableCache.enabled')}
        </Alert>
      </Snackbar>
    </HasPermission>
  );
};

export default DisableCacheSwitch;
